import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageBanner from '../components/PageBanner';
import { publicApi } from '../api/client';
import { Loader2, Star, Clock, MapPin, Check, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import BookingForm from '../components/BookingForm';
import { useSettings } from '../context/SettingsContext';

const CruiseDetail = () => {
  const { slug } = useParams();
  const [cruise, setCruise] = useState(null);
  const [loading, setLoading] = useState(true);
  const settings = useSettings();

  useEffect(() => {
    setLoading(true);
    publicApi.getCruise(slug).then((d) => { setCruise(d); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 text-center"><Loader2 className="animate-spin inline text-[#304b71]" size={32} /></div>;
  if (!cruise) return <div className="py-32 text-center text-slate-500">Cruise not found. <Link to="/cruises" className="text-[#304b71] underline">Back to all cruises</Link></div>;

  return (
    <>
      <PageBanner
        title={cruise.name}
        subtitle={cruise.short_desc}
        image={cruise.image}
        crumbs={[{ label: 'Nile Cruises', to: '/cruises' }, { label: cruise.name }]}
      />

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="flex flex-wrap gap-3 mb-6">
              <span className="bg-[#304b71] text-white px-3 py-1 rounded-full text-xs font-semibold">{cruise.category}</span>
              <span className="bg-[#f6f8fa] border border-slate-200 text-slate-700 px-3 py-1 rounded-full text-xs inline-flex items-center gap-1"><Clock size={11} /> {cruise.duration}</span>
              <span className="bg-[#f6f8fa] border border-slate-200 text-slate-700 px-3 py-1 rounded-full text-xs inline-flex items-center gap-1"><MapPin size={11} /> {cruise.route}</span>
              <span className="bg-[#f6f8fa] border border-slate-200 text-slate-700 px-3 py-1 rounded-full text-xs inline-flex items-center gap-1"><Star size={11} fill="#304b71" className="text-[#304b71]" /> {cruise.rating?.toFixed(1) || '4.9'}</span>
            </div>

            <h2 className="font-serif text-3xl text-[#304b71] mb-4">Overview</h2>
            <p className="text-slate-600 leading-relaxed whitespace-pre-line">{cruise.description}</p>

            {cruise.gallery && cruise.gallery.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-8">
                {cruise.gallery.map((g, i) => (
                  <img key={i} src={g} alt="" className="w-full h-40 object-cover rounded-xl" />
                ))}
              </div>
            )}

            {cruise.highlights?.length > 0 && (
              <div className="mt-10">
                <h2 className="font-serif text-2xl text-[#304b71] mb-4">Highlights</h2>
                <ul className="grid md:grid-cols-2 gap-3">
                  {cruise.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-700"><Check size={18} className="text-[#6a8aa4] flex-shrink-0 mt-0.5" /> {h}</li>
                  ))}
                </ul>
              </div>
            )}

            {cruise.amenities?.length > 0 && (
              <div className="mt-10">
                <h2 className="font-serif text-2xl text-[#304b71] mb-4">Onboard Amenities</h2>
                <div className="flex flex-wrap gap-2">
                  {cruise.amenities.map((a, i) => <span key={i} className="bg-[#f6f8fa] border border-slate-200 px-4 py-2 rounded-full text-sm text-slate-700">{a}</span>)}
                </div>
              </div>
            )}

            {cruise.itinerary?.length > 0 && (
              <div className="mt-10">
                <h2 className="font-serif text-2xl text-[#304b71] mb-4">Sample Itinerary</h2>
                <div className="space-y-4">
                  {cruise.itinerary.map((d, i) => (
                    <div key={i} className="border-l-2 border-[#a6b2b5] pl-5 py-1">
                      <div className="text-xs uppercase tracking-widest text-[#6a8aa4]">{d.day}</div>
                      <h3 className="font-serif text-lg text-[#304b71] mt-1">{d.title}</h3>
                      <p className="text-slate-600 text-sm mt-1">{d.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
                <div className="text-xs uppercase tracking-widest text-slate-500">{cruise.price_label}</div>
                <div className="font-serif text-4xl text-[#304b71] flex items-baseline gap-2 mt-1">
                  ${cruise.price?.toLocaleString()}
                  {cruise.old_price && <span className="text-lg text-slate-400 line-through font-sans">${cruise.old_price?.toLocaleString()}</span>}
                </div>
                <div className="text-xs text-slate-500">per person / USD</div>

                <div className="mt-6 border-t border-slate-100 pt-6">
                  <h3 className="font-serif text-lg text-[#304b71] mb-4">Enquire about this cruise</h3>
                  <BookingForm cruiseId={cruise.id} referenceName={cruise.name} defaultSubject={`Cruise: ${cruise.name}`} />
                </div>

                <div className="mt-6 pt-6 border-t border-slate-100 text-center">
                  <div className="text-xs text-slate-500 mb-2">Prefer to talk?</div>
                  <Button asChild variant="outline" className="w-full border-[#304b71] text-[#304b71] hover:bg-[#304b71] hover:text-white"><a href={`tel:${settings.phone_raw}`}><Phone size={14} className="mr-2" /> {settings.phone}</a></Button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

export default CruiseDetail;
